'use client';

import { __rest, __assign } from '../_virtual/_tslib.js';
import React__default, { useContext, useState, useRef, useEffect } from 'react';
import { validateNoSpecialChars, validateI11lPhoneNumber, validateVietNamPhoneNumber, validateUrl, validatePhoneNumber, validateEmail } from '../Utils/validator.js';

/**
 * FormContext
 *
 * Store all necessary states, setters, handlers....
 */
var FormContext = React__default.createContext({
    values: {},
    touched: {},
    onSubmit: function () { },
    setValues: function () { },
    setTouched: function () { },
    rules: [],
    errors: {},
    setErrors: function () { },
    advancedValidate: function () {
        return {};
    },
    onAnyChange: function () { },
    validateForm: function () { return false; },
    submitForm: function () { },
    hasSubmittedRef: {
        current: false
    },
    forceShowError: false
});
/**
 * FormManager
 * Entry point of Form
 *
 * Accepts initial values, settings as props
 */
function FormManager(_a) {
    var initialValues = _a.initialValues, onSubmit = _a.onSubmit, children = _a.children, _b = _a.rules, rules = _b === void 0 ? [] : _b, _c = _a.advancedValidate, advancedValidate = _c === void 0 ? function () {
        return {};
    } : _c, _d = _a.onAnyChange, onAnyChange = _d === void 0 ? function () { } : _d, onValidation = _a.onValidation, _e = _a.forceShowError, forceShowError = _e === void 0 ? false : _e, user = _a.user;
    var _f = useState(initialValues), values = _f[0], setValues = _f[1];
    var _g = useState({}), errors = _g[0], setErrors = _g[1];
    var _h = useState({}), touched = _h[0], setTouched = _h[1];
    var hasSubmittedRef = useRef(false);
    var parseJSON = function (str) {
        try {
            return JSON.parse(str);
        }
        catch (e) {
            return str;
        }
    };
    var validateForm = function () {
        var errorObj = {};
        rules.map(function (rule) {
            var field = rule.field;
            var value = values[field];
            // get value text of textEditor
            if (field.includes('description')) {
                // {"blocks":[{"key":"dfj3h","text":"Bold text should be bolded","type":"unstyled","depth":0,"inlineStyleRanges":[{"offset":0,"length":9,"style":"BOLD"}],"entityRanges":[],"data":{}}],"entityMap":{}}
                value = parseJSON(value);
                if (typeof value === 'object') {
                    value = value.blocks
                        .map(function (block) { return (block.text.trim() ? block.text : '\n'); })
                        .join('\n')
                        .trim();
                }
            }
            for (var i = 0; i < rule.rules.length; i++) {
                var ruleItem = rule.rules[i];
                if (errorObj[field]) {
                    break;
                }
                switch (ruleItem.check) {
                    case 'required':
                        if (!value) {
                            errorObj[field] = ruleItem.msg;
                            return;
                        }
                        if (typeof value === 'object' &&
                            Object.keys(value).every(function (key) {
                                var fieldValue = value[key];
                                if (typeof fieldValue === 'string') {
                                    fieldValue = fieldValue.trim();
                                }
                                return !fieldValue;
                            })) {
                            errorObj[field] = ruleItem.msg;
                            return;
                        }
                        if (Array.isArray(value) && value.length === 0) {
                            errorObj[field] = ruleItem.msg;
                            return;
                        }
                        break;
                    case 'email':
                        if (value && !validateEmail(value)) {
                            errorObj[field] = ruleItem.msg;
                        }
                        break;
                    case 'phone':
                        if (value && !validatePhoneNumber(value)) {
                            errorObj[field] = ruleItem.msg;
                        }
                        break;
                    case 'url':
                        if (value && !validateUrl(value)) {
                            errorObj[field] = ruleItem.msg;
                        }
                        break;
                    case 'vn-phone':
                        if (value && value.includes('+84') && !validateVietNamPhoneNumber(value)) {
                            errorObj[field] = ruleItem.msg;
                        }
                        break;
                    case 'i11l-phone':
                        if (value && !value.includes('+84') && !validateI11lPhoneNumber(value)) {
                            errorObj[field] = ruleItem.msg;
                        }
                        break;
                    case 'duplicateEmail':
                        // check email Basic info and email Reference
                        if (value && value === (user === null || user === void 0 ? void 0 : user.userName)) {
                            errorObj[field] = ruleItem.msg;
                        }
                        break;
                    case 'noSpecialChars':
                        // check email Basic info and email Reference
                        if (value && validateNoSpecialChars(value)) {
                            errorObj[field] = ruleItem.msg;
                        }
                        break;
                }
            }
        });
        var advancedErrors = advancedValidate(values);
        var mergedErrors = __assign(__assign({}, errorObj), advancedErrors);
        if (onValidation) {
            onValidation(mergedErrors);
        }
        setErrors(mergedErrors);
        return Object.values(mergedErrors).some(function (item) { return item !== ''; });
    };
    var submitForm = function () {
        hasSubmittedRef.current = true;
        var newTouched = __assign({}, touched);
        Object.keys(values).map(function (field) { return (newTouched[field] = true); });
        setTouched(newTouched);
        var hasError = validateForm();
        if (!hasError) {
            onSubmit(values);
        }
    };
    useEffect(function () {
        validateForm();
    }, [values]);
    return (React__default.createElement(FormContext.Provider, { value: {
            values: values,
            touched: touched,
            onSubmit: onSubmit,
            setValues: setValues,
            setTouched: setTouched,
            rules: rules,
            errors: errors,
            setErrors: setErrors,
            advancedValidate: advancedValidate,
            onAnyChange: onAnyChange,
            validateForm: validateForm,
            submitForm: submitForm,
            hasSubmittedRef: hasSubmittedRef,
            forceShowError: forceShowError
        } }, typeof children === 'function' ? children({ validateForm: validateForm, submitForm: submitForm, values: values }) : children));
}
/**
 * FMForm
 *
 * Handle onSubmit, validation
 */
var FMForm = React__default.forwardRef(function FMFormComponent(_a, ref) {
    var children = _a.children, rest = __rest(_a, ["children"]);
    var _b = useContext(FormContext), submitForm = _b.submitForm, hasSubmittedRef = _b.hasSubmittedRef;
    return (React__default.createElement("form", __assign({ ref: ref, onSubmit: function (e) {
            e.preventDefault();
            submitForm();
            hasSubmittedRef.current = true;
        } }, rest), children));
});
/**
 * Hook to retrieve FormContext
 */
function useFMFormContext() {
    return useContext(FormContext);
}

export { FMForm, FormContext, FormManager, useFMFormContext };
//# sourceMappingURL=index.js.map
